import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ShieldOff } from 'lucide-react';

const API = '/api';

export default function SuppressedContactsPage() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [blocked, setBlocked] = useState([]);
  const [optOutEmail, setOptOutEmail] = useState('');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/suppression/list?q=${encodeURIComponent(q)}&status=${status}`, { withCredentials: true });
      setRows(r.data.rows || []); setCounts(r.data.counts || {}); setBlocked(r.data.recent_blocked || []);
    } catch { toast.error('Failed to load suppression registry'); }
  }, [q, status]);
  useEffect(() => { load(); }, [load]);

  const optOut = async () => {
    const email = optOutEmail.trim();
    if (!email.includes('@')) { toast.error('Enter a valid email'); return; }
    if (!window.confirm(`Permanently block all emails to ${email}?`)) return;
    setBusy('optout');
    try {
      const r = await axios.post(`${API}/suppression/opt-out`, { email }, { withCredentials: true });
      toast.success(`${email} is now Do-Not-Contact (${r.data.contacts_removed} contact record(s) removed)`);
      setOptOutEmail(''); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Opt-out failed'); }
    finally { setBusy(''); }
  };

  const restore = async (rec) => {
    let body = { recreate_contact: true };
    if (rec.status === 'DO_NOT_CONTACT') {
      const reason = window.prompt('This contact explicitly opted out. Admin override reason (required):');
      if (!reason) return;
      body = { ...body, override: true, override_reason: reason };
    } else if (!window.confirm(`Restore ${rec.name || rec.normalized_email} as an active contact?`)) return;
    setBusy(rec.id);
    try {
      await axios.post(`${API}/suppression/${rec.id}/restore`, body, { withCredentials: true });
      toast.success('Contact restored'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Restore failed'); }
    finally { setBusy(''); }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="suppressed-contacts-page">
      <div className="flex items-center gap-2 mb-1">
        <ShieldOff className="w-5 h-5 text-rose-600" />
        <h1 className="text-xl font-bold text-slate-900">Suppressed Contacts</h1>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Deliberately removed contacts are retained here so AI mapping, discovery, enrichment, imports and outreach can never silently re-add or email them. Do-Not-Contact records are never auto-reversed.
      </p>
      <div className="flex gap-4 mb-4 text-sm">
        <span data-testid="count-total"><b>{counts.total_active ?? 0}</b> active</span>
        <span className="text-rose-700"><b>{counts.do_not_contact ?? 0}</b> Do Not Contact</span>
        <span className="text-slate-600"><b>{counts.manually_removed ?? 0}</b> Manually Removed</span>
        <span className="text-amber-700" data-testid="count-blocked-sends"><b>{counts.blocked_sends ?? 0}</b> emails stopped by the send gate</span>
      </div>
      <div className="flex gap-2 mb-4 items-center">
        <Input placeholder="Opt-out an email address (e.g. someone replied 'stop emailing me')" value={optOutEmail} onChange={(e) => setOptOutEmail(e.target.value)} className="max-w-md h-8 text-sm" data-testid="opt-out-email-input" />
        <Button size="sm" className="h-8 text-xs bg-rose-600 hover:bg-rose-700" disabled={busy === 'optout'} onClick={optOut} data-testid="opt-out-submit">Block permanently</Button>
      </div>
      <div className="flex gap-2 mb-4">
        <Input placeholder="Search name / email / company / reason…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm h-8 text-sm" data-testid="suppression-search" />
        {['', 'DO_NOT_CONTACT', 'MANUALLY_REMOVED'].map((s) => (
          <Button key={s || 'all'} size="sm" variant={status === s ? 'default' : 'outline'} className="h-8 text-xs" data-testid={`suppression-filter-${s === '' ? 'all' : s === 'DO_NOT_CONTACT' ? 'dnc' : 'mr'}`} onClick={() => setStatus(s)}>
            {s === '' ? 'All' : s === 'DO_NOT_CONTACT' ? 'Do Not Contact' : 'Manually Removed'}
          </Button>
        ))}
      </div>
      <Card className="border border-slate-200">
        <CardContent className="p-0 divide-y divide-slate-100">
          {rows.map((r) => (
            <div key={r.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`suppressed-row-${r.id}`}>
              <div className="min-w-0 flex-1">
                <span className="text-sm font-medium text-slate-800">{r.name || r.normalized_email || '(no name)'}</span>
                {r.name && <span className="ml-2 text-xs text-slate-500 font-mono">{r.normalized_email}</span>}
                <div className="text-[11px] text-slate-400">{[r.company_name, r.reason, String(r.suppressed_at || '').slice(0, 10), `by ${r.suppressed_by || r.source}`].filter(Boolean).join(' · ')}</div>
              </div>
              <Badge className={`text-[10px] ${r.status === 'DO_NOT_CONTACT' ? 'bg-rose-100 text-rose-700 border border-rose-300' : 'bg-slate-100 text-slate-600 border border-slate-300'}`}>
                {r.status === 'DO_NOT_CONTACT' ? 'Do Not Contact' : 'Manually Removed'}
              </Badge>
              <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy === r.id} onClick={() => restore(r)} data-testid={`restore-${r.id}`}>
                {r.status === 'DO_NOT_CONTACT' ? 'Admin Override…' : 'Restore'}
              </Button>
            </div>
          ))}
          {rows.length === 0 && <p className="text-sm text-slate-400 p-6 text-center">No suppressed contacts.</p>}
        </CardContent>
      </Card>
      <h2 className="text-base font-semibold text-slate-800 mt-8 mb-2" data-testid="blocked-sends-heading">Recently stopped emails</h2>
      <p className="text-xs text-slate-500 mb-3">Every send attempt to a deleted, unknown, blocklisted or Do-Not-Contact address is refused before it reaches Gmail and logged here.</p>
      <Card className="border border-slate-200">
        <CardContent className="p-0 divide-y divide-slate-100">
          {blocked.map((b, i) => (
            <div key={i} className="px-4 py-2 text-xs" data-testid={`blocked-row-${i}`}>
              <span className="font-mono text-slate-800">{b.to}</span>
              <span className="text-slate-400"> · {String(b.at || '').slice(0, 16).replace('T', ' ')} · {b.purpose}</span>
              <div className="text-amber-700">{b.reason}</div>
            </div>
          ))}
          {blocked.length === 0 && <p className="text-sm text-slate-400 p-6 text-center">No blocked sends yet.</p>}
        </CardContent>
      </Card>
    </div>
  );
}
